import TomSelect from "tom-select";

/** @type {{ ajaxUrl: string; nonce: string }} */
const ListingData = window.ListingData;

export class Listing {
  controller = null;
  summaryController = null;
  page = 1;
  hasMore = false;
  observer = null;
  tomSelects = new Map();
  lastSummaryQuery = null;

  constructor(root) {
    this.root = root;
    this.baseArgs = JSON.parse(root.dataset.queryArgs || "{}");
    this.filterGroupSchema = JSON.parse(root.dataset.filterGroups || "{}");
    this.isSearchPage = !!root.dataset.isSearchPage;
    this.infiniteScroll = !!root.dataset.infiniteScroll;
    this.resultsEl = root.querySelector(".js--listing-results");
    this.metaEl = root.querySelector(".js--listing-meta");

    if (!this.resultsEl) {
      return;
    }

    this.filtersEl = root.querySelector(".js--listing-filters");

    const list = this.resultsEl.querySelector(".listing__results-list");
    this.hasMore = list?.dataset.hasMore === "true";

    if (this.filtersEl) {
      this.filterGroupsEl = this.filtersEl.querySelector(".js--filter-groups");
      this.setupFilterDelegation();
      this.setupFilters();
    }

    this.setupPagination();

    if (this.isSearchPage) {
      this.summarySectionEl = this.root.querySelector(
        ".js--ai-summary-section",
      );
      this.summaryEl = this.root.querySelector(".js--ai-summary");
      this.sourcesEl = this.root.querySelector(".js--ai-sources");
      if (this.summaryEl?.dataset.searchId) {
        this.lastSummaryQuery = this.getQuery();
        this.fetchAiSummary();
      }

      const searchInput = this.root.querySelector('[name="q"]');
      if (searchInput?.value.trim()) {
        searchInput.focus();
      }
    }

    const toggleEl = this.root.querySelector(".js--view-toggle");
    if (toggleEl) {
      this.setupViewToggle(toggleEl);
    }

    this.observeLastItem();
  }

  setupPagination() {
    this.resultsEl.addEventListener("click", (e) => {
      const btn = e.target.closest(".js--pagination-btn");
      if (!btn) return;
      const page = parseInt(btn.dataset.page, 10);
      if (page) this.fetchResults(page, { scroll: true });
    });
  }

  setupViewToggle(toggleEl) {
    this.toggleEl = toggleEl;
    const savedView =
      new URLSearchParams(window.location.search).get("view") || "grid";
    this.applyView(savedView);

    toggleEl.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-view]");
      if (!btn) return;
      const view = btn.dataset.view;
      this.applyView(view);

      const params = new URLSearchParams(window.location.search);
      if (view === "grid") {
        params.delete("view");
      } else {
        params.set("view", view);
      }
      const qs = params.toString();
      history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
    });
  }

  applyView(view) {
    this.currentView = view;
    this.resultsEl.classList.toggle("is-list-view", view === "list");
    if (this.toggleEl) {
      this.toggleEl.querySelectorAll("[data-view]").forEach((b) => {
        b.classList.toggle("is-active", b.dataset.view === view);
        b.setAttribute(
          "aria-pressed",
          b.dataset.view === view ? "true" : "false",
        );
      });
    }
  }

  setupFilterDelegation() {
    // Use event delegation for form submit, checkbox changes, and clear buttons
    // to avoid duplicate listeners after AJAX swaps.
    this.filtersEl.addEventListener("submit", (e) => {
      if (e.target.matches("form")) {
        e.preventDefault();
        if (this.isSearchPage) {
          // On the search page a new text query starts fresh — clear active filters.
          this.filtersEl
            .querySelectorAll('input[type="checkbox"]:checked')
            .forEach((input) => {
              input.checked = false;
            });
          this.tomSelects.forEach((ts) => ts.clear(true));
        }
        this.fetchResults();
      }
    });

    this.filtersEl.addEventListener("change", (e) => {
      if (e.target.matches('input[type="checkbox"]')) {
        this.fetchResults();
      }
    });

    this.filtersEl.addEventListener("click", (e) => {
      const clearBtn = e.target.closest(".js--clear-filters");
      if (clearBtn) {
        this.filtersEl
          .querySelectorAll('input[type="checkbox"]:checked')
          .forEach((input) => {
            input.checked = false;
          });
        this.tomSelects.forEach((ts) => ts.clear(true));
        if (clearBtn.dataset.clearQuery) {
          const searchInput = this.root.querySelector('[name="q"]');
          if (searchInput) searchInput.value = "";
        }
        this.fetchResults();
      }
    });
  }

  setupFilters() {
    if (!this.filtersEl) return;

    this.tomSelects.forEach((ts) => ts.destroy());
    this.tomSelects.clear();

    this.filtersEl.querySelectorAll(".js--filters-combobox").forEach((el) => {
      const ts = new TomSelect(el, {
        placeholder: el.dataset.placeholder || "Select…",
        closeAfterSelect: true,
      });

      el._originalOptions = Object.values(ts.options).map((o) => ({ ...o }));

      const pillsEl = document.createElement("div");
      pillsEl.className = "ts-pills";
      ts.wrapper.insertAdjacentElement("afterend", pillsEl);

      const addPill = (value) => {
        const label = ts.options[value]?.text || value;
        const pill = document.createElement("div");
        pill.className = "ts-pill";
        pill.dataset.value = String(value);
        const removeBtn = document.createElement("button");
        removeBtn.type = "button";
        removeBtn.className = "ts-pill__remove";
        removeBtn.setAttribute("aria-label", `Remove ${label}`);
        removeBtn.innerHTML =
          '<svg aria-hidden="true" focusable="false"><use xlink:href="#close"></use></svg>';
        removeBtn.addEventListener("click", () => ts.removeItem(String(value)));
        pill.append(document.createTextNode(label), removeBtn);
        pillsEl.appendChild(pill);
      };

      const removePill = (value) => {
        pillsEl
          .querySelector(`[data-value="${CSS.escape(String(value))}"]`)
          ?.remove();
      };

      // Add pills for any pre-selected items (e.g. after an AJAX swap).
      // item_add does not fire during TomSelect construction, so we seed
      // pills manually here and only wire fetchResults for future changes.
      ts.items.forEach(addPill);
      ts.on("item_add", (value) => {
        addPill(value);
        this.fetchResults();
      });
      ts.on("item_remove", (value) => {
        removePill(value);
        this.fetchResults();
      });

      this.tomSelects.set(el, ts);
    });
  }

  buildParams(page) {
    const query = this.getQuery();
    const params = new URLSearchParams({
      action: "listing_filter",
      nonce: ListingData.nonce,
      args: JSON.stringify({ ...this.baseArgs, paged: page }),
      filter_groups: JSON.stringify(this.filterGroupSchema),
      q: query,
      pg: page,
      isSearchPage: this.isSearchPage ? 1 : 0,
      infiniteScroll: this.infiniteScroll ? 1 : 0,
    });

    this.filtersEl
      ?.querySelectorAll('input[type="checkbox"]:checked')
      .forEach((input) => {
        params.append(input.name, input.value);
      });

    // Read from ts.items rather than select.selectedOptions — TomSelect
    // updates its internal items array before firing item_add/item_remove,
    // but syncs back to the underlying <select> after the event. Reading
    // selectedOptions at item_add time would miss the just-added value.
    this.tomSelects.forEach((ts, el) => {
      ts.items.forEach((value) => {
        params.append(el.name, value);
      });
    });

    return params;
  }

  getQuery() {
    const input = this.root.querySelector('[name="q"]');
    return input ? input.value.trim() : "";
  }

  setLoading(loading) {
    this.root.classList.toggle("is-loading", loading);
    this.filtersEl
      ?.querySelectorAll('input[type="checkbox"]')
      .forEach((input) => (input.disabled = loading));
    this.tomSelects.forEach((ts) => (loading ? ts.lock() : ts.unlock()));
    if (this.clearBtn) this.clearBtn.disabled = loading;
  }

  machineName(input) {
    return input.name.replace(/^[^:]+:/, "").replace(/\[\]$/, "");
  }

  syncUrl(page = this.page) {
    const params = new URLSearchParams(window.location.search);

    // Sync q from the current input value.
    const query = this.getQuery();
    if (query) {
      params.set("q", query);
    } else {
      params.delete("q");
    }

    // Sync pg — omit for page 1 to keep URLs clean.
    if (page > 1) {
      params.set("pg", page);
    } else {
      params.delete("pg");
    }

    // Remove existing filter params. The URL key is _machine_name[] so we delete
    // with the exact bracket string since URLSearchParams treats it as a literal key.
    this.filtersEl
      ?.querySelectorAll('input[type="checkbox"]')
      .forEach((input) => params.delete(`_${this.machineName(input)}[]`));
    this.tomSelects.forEach((_, el) =>
      params.delete(`_${this.machineName(el)}[]`),
    );

    // Build filter params manually so brackets stay literal (not %5B%5D).
    const filterParts = [];
    this.filtersEl
      ?.querySelectorAll('input[type="checkbox"]:checked')
      .forEach((input) => {
        filterParts.push(
          `_${this.machineName(input)}[]=${encodeURIComponent(input.value)}`,
        );
      });
    this.tomSelects.forEach((ts, el) => {
      ts.items.forEach((value) => {
        filterParts.push(
          `_${this.machineName(el)}[]=${encodeURIComponent(value)}`,
        );
      });
    });

    const base = params.toString();
    const qs = [base, filterParts.join("&")].filter(Boolean).join("&");
    history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
  }

  observeLastItem() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (!this.hasMore || !this.infiniteScroll) return;

    const list = this.resultsEl.querySelector(".listing__results-list");
    const lastItem = list?.lastElementChild;
    if (!lastItem) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.observer.disconnect();
          this.observer = null;
          this.loadMore();
        }
      },
      { rootMargin: "200px" },
    );

    this.observer.observe(lastItem);
  }

  async fetchResults(page = 1, { scroll = false } = {}) {
    // Abort a streaming summary only when the query itself is changing.
    // Filter-only changes leave the summary running — it's still valid.
    if (this.summaryController && this.getQuery() !== this.lastSummaryQuery) {
      this.summaryController.abort();
      this.summaryController = null;
    }

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    this.page = page;
    this.setLoading(true);

    try {
      const res = await fetch(
        `${ListingData.ajaxUrl}?${this.buildParams(page)}`,
        { signal: this.controller.signal },
      );
      const data = await res.json();

      if (data.success) {
        const toggle = this.resultsEl.querySelector(".js--view-toggle");
        this.resultsEl.innerHTML = data.data.html;

        if (this.filterGroupsEl && data.data.filters_html) {
          this.filterGroupsEl.outerHTML = data.data.filters_html;
          this.filterGroupsEl =
            this.filtersEl?.querySelector(".js--filter-groups");
          this.setupFilters();
        }

        this.metaEl = this.resultsEl.querySelector(".js--listing-meta");
        if (toggle && this.metaEl) {
          this.metaEl.querySelector(".js--view-toggle")?.remove();
          this.metaEl.append(toggle);
        }
        if (this.currentView) this.applyView(this.currentView);
        this.hasMore = data.data.has_more;
        this.syncUrl(page);
        this.observeLastItem();
        if (scroll) {
          this.resultsEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }

        const sources = data.data.summary_sources ?? [];
        const currentQuery = this.getQuery();
        if (this.summaryEl && this.summarySectionEl) {
          if (sources.length >= 3 && data.data.search_id) {
            this.summarySectionEl.style.display = "";
            this.summaryEl.dataset.searchId = data.data.search_id;
            this.summaryEl.dataset.summarySources = JSON.stringify(sources);
            // Only fetch AI summary if the query text has changed
            if (currentQuery !== this.lastSummaryQuery) {
              this.lastSummaryQuery = currentQuery;
              this.fetchAiSummary();
            }
          } else if (currentQuery !== this.lastSummaryQuery) {
            // Query changed but not enough sources - hide and clear the summary
            this.summarySectionEl.style.display = "none";
            this.summaryEl.innerHTML = "";
            this.summaryEl.classList.remove("has-summary");
            if (this.sourcesEl) this.sourcesEl.innerHTML = "";
            this.lastSummaryQuery = currentQuery;
          }
          // else: query unchanged and not enough sources - preserve existing summary
        }
      }
      this.setLoading(false);
    } catch (err) {
      // Aborted requests are superseded by a newer fetch — leave the loading
      // state in place so the replacement request's UI isn't interrupted.
      if (err.name !== "AbortError") this.setLoading(false);
    } finally {
      this.controller = null;
    }
  }

  async fetchAiSummary() {
    if (!this.summaryEl || !window.CludoSearch) return;

    const queryId = this.summaryEl.dataset.searchId;
    const sources = JSON.parse(this.summaryEl.dataset.summarySources || "[]");
    const query = this.getQuery();

    if (!queryId || sources.length < 3 || !query) return;

    if (this.summaryController) this.summaryController.abort();
    this.summaryController = new AbortController();

    this.summaryEl.classList.add("is-loading");
    this.summaryEl.innerHTML = `<div class="search-ai-summary__loading"><span></span><span></span><span></span></div>`;
    if (this.sourcesEl) this.sourcesEl.innerHTML = "";

    const body = new URLSearchParams({
      action: "cludo_ai_summary_stream",
      nonce: window.CludoSearch.nonce,
      query,
      queryId,
      sources: JSON.stringify(sources),
    });

    try {
      const res = await fetch(ListingData.ajaxUrl, {
        method: "POST",
        body,
        signal: this.summaryController.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error("Stream failed");
      }

      // Clear loading state and prepare for streaming text
      this.summaryEl.classList.remove("is-loading");
      this.summaryEl.classList.add("has-summary", "is-streaming");
      this.summaryEl.innerHTML = `<p class="search-ai-summary__text"></p>`;

      const textEl = this.summaryEl.querySelector(".search-ai-summary__text");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // Check for insufficient context marker
        if (buffer.includes("__INSUFFICIENT_CONTEXT__")) {
          if (this.summarySectionEl) {
            this.summarySectionEl.style.display = "none";
          }
          this.summaryEl.innerHTML = "";
          if (this.sourcesEl) this.sourcesEl.innerHTML = "";
          break;
        }

        // Check for source pages marker
        if (buffer.includes("__SOURCE_PAGES__")) {
          const match = buffer.match(/__SOURCE_PAGES__(.+)/);
          if (match) {
            try {
              const sourcePages = JSON.parse(match[1].trim());
              if (this.sourcesEl && sourcePages.length) {
                this.sourcesEl.innerHTML = `<ol class="search-ai-summary__list">${sourcePages.map((s) => `<li><a href="${s.url}">${s.title}</a></li>`).join("")}</ol>`;
              }
              // Remove the marker from the displayed text
              buffer = buffer.replace(/__SOURCE_PAGES__.+/, "");
              textEl.textContent = buffer;
            } catch {
              // Invalid JSON, ignore
            }
          }
          break;
        }

        // Append chunk to the summary text
        textEl.textContent = buffer;
      }
    } catch (err) {
      // On a query change the summary is aborted intentionally — leave existing
      // content in place so there's no flash of empty before the new summary loads.
      // Clear only on genuine errors.
      if (err.name !== "AbortError") {
        this.summaryEl.innerHTML = "";
        if (this.sourcesEl) this.sourcesEl.innerHTML = "";
      }
    } finally {
      this.summaryEl.classList.remove("is-loading", "is-streaming");
      this.summaryController = null;
    }
  }

  async loadMore() {
    if (this.controller || !this.hasMore) return;
    this.controller = new AbortController();
    this.page++;

    try {
      const res = await fetch(
        `${ListingData.ajaxUrl}?${this.buildParams(this.page)}`,
        { signal: this.controller.signal },
      );
      const data = await res.json();

      if (data.success) {
        const list = this.resultsEl.querySelector(".listing__results-list");
        if (list) {
          list.insertAdjacentHTML("beforeend", data.data.items_html);
        }
        this.hasMore = data.data.has_more;
        this.observeLastItem();
      }
    } catch (err) {
      if (err.name !== "AbortError") this.page--;
    } finally {
      this.controller = null;
    }
  }
}
